/**
 * AdminBanner Component
 *
 * Displays an amber banner when an admin is viewing a user's portfolio.
 * Contains navigation back to users list and position history access.
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, History, Shield } from "lucide-react";

// ============================================================================
// Types
// ============================================================================

export interface AdminBannerProps {
  /** The account code being viewed (for display) */
  accountCode: string | undefined;
  /** The user ID being viewed (for fallback display) */
  userId: string;
  /** The user's display name */
  userName?: string | null;
  /** The user's email */
  userEmail?: string | null;
  /** Callback when Position History button is clicked */
  onOpenPositionHistory: () => void;
}

// ============================================================================
// Component
// ============================================================================

const AdminBanner: React.FC<AdminBannerProps> = ({
  accountCode,
  userId,
  userName,
  userEmail,
  onOpenPositionHistory,
}) => {
  const navigate = useNavigate();

  const handleBackToUsers = () => {
    navigate("/admin/users");
  };

  return (
    <div className="bg-amber-500 text-black px-4 py-2 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Shield className="w-4 h-4" />
        <span className="font-medium text-sm">
          Admin View: {userName || accountCode || userId}
          {userEmail && (
            <span className="font-normal opacity-75 ml-1">
              ({userEmail})
            </span>
          )}
          <span className="font-mono font-normal opacity-60 ml-1 text-xs">
            [{userId}]
          </span>
        </span>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={onOpenPositionHistory}
          className="flex items-center gap-1 px-3 py-1 bg-amber-600 hover:bg-amber-700 rounded text-sm font-medium transition-colors"
        >
          <History className="w-4 h-4" />
          Position History
        </button>
        <button
          onClick={handleBackToUsers}
          className="flex items-center gap-1 px-3 py-1 bg-amber-600 hover:bg-amber-700 rounded text-sm font-medium transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Users
        </button>
      </div>
    </div>
  );
};

export default AdminBanner;

